<template>
  <div class="flex flex-wrap -mx-10">
    <div class="hidden lg:block lg:w-1/3 px-10">
      <profile-card :content="content" />
    </div>

    <div class="w-full lg:w-2/3 px-10">
      <div class="flex flex-wrap items-center justify-between pb-10">
        <div class="flex-grow">
          <div>
            <ol class="list-reset flex">
              <li>
                <a :href="`/area/${content.subjectAreaId}`" class="font-bold">{{
                  content.subjectAreaName
                }}</a>
              </li>
            </ol>
          </div>
          <h1 class="mb-0 mt-0">
            {{ content.name }}
          </h1>
        </div>
        <div
          v-if="!hasPurchased"
          class="shadow-lg bg-white rounded-full px-6 py-2 flex-shrink font-bold text-green text-2xl"
        >
          ${{ cost }}
        </div>
      </div>

      <profile-card
        :content="content"
        full-width
        class="hidden md:block lg:hidden"
      />

      <div class="md:pt-16 lg:pt-0" />
      <div v-if="!hasPurchased">
        <h3 class="mb-0">
          Lesson Description
        </h3>
        <div class="rich-text" v-html="content.description" />
        <div class="relative mt-10" style="min-height: 400px;">
          <div class="absolute inset-0 bg-gray-200 opacity-90" />
          <div class="absolute w-full text-center lg:px-20 px-5 pb-16 top-0">
            <d-icon icon="lock" class="text-6xl mb-5" />
            <p class="font-bold text-3xl mb-5">
              Would you like to purchase the lesson "{{ content.name }}" for ${{
                cost
              }}?
            </p>
            <d-btn color="secondary-500" @click="launchBuyNowModal()">
              Buy Now
            </d-btn>
          </div>
        </div>
      </div>
      <div v-else-if="loadingScreen" class="flex justify-center py-10">
        <d-loader />
      </div>
      <div v-else>
        <transition name="fade" mode="out-in">
          <div v-if="showCompletedScreen" class="py-10">
            <h3>Congratulations on completing {{ content.name }}!</h3>
            <div v-if="earnedCertificate">
              <p class="font-bold text-xl">
                This lesson was completed on
                {{ earnedCertificate.completedDate }}
              </p>
              <div class="py-4">
                <d-btn
                  class="lg:mt-0 mt-4 mr-3"
                  color="primary-500"
                  size="sm"
                  :href="earnedCertificate.certificateUrl"
                  target="_blank"
                >
                  Download Certificate
                </d-btn>
                <d-btn
                  outline
                  class="lg:mt-0 mt-4 mr-3"
                  color="primary-500"
                  size="sm"
                  @click="startLessonAgainAfterCompletion()"
                >
                  Review the Lesson again
                </d-btn>
                <d-btn
                  outline
                  class="lg:mt-0 mt-4"
                  color="primary-500"
                  size="sm"
                  :href="`/area/${content.subjectAreaId}`"
                >
                  Back to Home
                </d-btn>
              </div>
            </div>
            <div v-else>
              <p>
                You have successfully completed all of the steps in this lesson.
              </p>
              <div class="py-4">
                <d-btn
                  class="lg:mt-0 mt-4 mr-3"
                  color="primary-500"
                  size="sm"
                  @click="startLessonAgainAfterCompletion()"
                >
                  Review the Lesson again
                </d-btn>
                <d-btn
                  outline
                  class="lg:mt-0 mt-4"
                  color="primary-500"
                  size="sm"
                  :href="`/area/${content.subjectAreaId}`"
                >
                  Back to Home
                </d-btn>
              </div>
            </div>
          </div>

          <div v-else-if="currentStep === 0" key="intro">
            <h3 class="mb-0">
              Lesson Description
            </h3>
            <div class="rich-text" v-html="content.description" />

            <div class="py-10">
              <d-btn color="secondary-500" @click="start()">
                Get Started
              </d-btn>
              <d-btn
                v-if="listingItemInteractions.length > 0 && !hasCompleted"
                color="secondary-500"
                class="mt-3 md:mt-0 md:mx-3"
                @click="goToLastSavedStep()"
              >
                Continue From Saved Progress
              </d-btn>
            </div>
          </div>

          <div v-else key="modules">
            <transition name="fade" mode="out-in">
              <quiz-module
                v-if="ListingItemType.QUIZ === currentStepContent.type"
                :key="`${currentStep}-quiz`"
                :content-item="currentStepContent"
              >
                <template #pagination="{checkIfPassed, passed, submitting}">
                  <div v-if="loadingNextStep" class="flex justify-center py-5">
                    <d-loader :size="35" />
                  </div>
                  <div v-else class="flex justify-between items-center py-10">
                    <d-btn color="secondary-500" @click="goBack()">
                      Back
                    </d-btn>
                    <div class="font-bold text-lg">
                      {{ currentStep }}/{{ totalSteps }}
                    </div>
                    <div>
                      <div v-if="passed">
                        <d-btn
                          v-if="currentStep !== totalSteps"
                          key="next"
                          color="secondary-500"
                          @click="goNext()"
                        >
                          Next
                        </d-btn>
                        <d-btn
                          v-else
                          key="finish"
                          color="secondary-500"
                          @click="processLastStep()"
                        >
                          Finish
                        </d-btn>
                      </div>
                      <div v-else>
                        <d-btn
                          key="submit"
                          color="secondary-500"
                          :loading="submitting"
                          @click="checkIfPassed()"
                        >
                          Submit Quiz
                        </d-btn>
                      </div>
                    </div>
                  </div>
                </template>
              </quiz-module>

              <exam-module
                v-else-if="ListingItemType.EXAM === currentStepContent.type"
                :key="`${currentStep}-exam`"
                :content-item="currentStepContent"
              >
                <template #pagination="{checkIfPassed, passed, submitting}">
                  <div v-if="loadingNextStep" class="flex justify-center py-5">
                    <d-loader :size="35" />
                  </div>
                  <div v-else class="flex justify-between items-center py-10">
                    <d-btn color="secondary-500" @click="goBack()">
                      Back
                    </d-btn>
                    <div class="font-bold text-lg">
                      {{ currentStep }}/{{ totalSteps }}
                    </div>
                    <div>
                      <div v-if="passed">
                        <d-btn
                          v-if="currentStep !== totalSteps"
                          key="next"
                          color="secondary-500"
                          @click="goNext()"
                        >
                          Next
                        </d-btn>
                        <d-btn
                          v-else
                          key="finish"
                          color="secondary-500"
                          @click="processLastStep()"
                        >
                          Finish
                        </d-btn>
                      </div>
                      <div v-else>
                        <d-btn
                          key="submit"
                          color="gray-400"
                          :loading="submitting"
                          disabled
                          @click="checkIfPassed()"
                        >
                          Next
                        </d-btn>
                      </div>
                    </div>
                  </div>
                </template>
              </exam-module>

              <content-module
                v-else
                :key="`${currentStep}-non-quiz`"
                :content-item="currentStepContent"
              >
                <template #pagination>
                  <div v-if="loadingNextStep" class="flex justify-center py-5">
                    <d-loader :size="35" />
                  </div>
                  <div v-else class="flex justify-between items-center py-10">
                    <d-btn color="secondary-500" @click="goBack()">
                      Back
                    </d-btn>
                    <div class="font-bold text-lg">
                      {{ currentStep }}/{{ totalSteps }}
                    </div>
                    <div>
                      <p v-if="!timerCompleted" style="width:150px">
                         Continue in: {{ minutes }}:{{ seconds }}
                      </p>

                      <d-btn
                        v-if="currentStep !== totalSteps"
                        key="next"
                        color="secondary-500"
                        :disabled="!timerCompleted"
                        @click="goNext()"
                      >
                        Next
                      </d-btn>
                      <d-btn
                        v-else
                        key="finish"
                        :disabled="!timerCompleted"
                        color="secondary-500"
                        @click="processLastStep()"
                      >
                        Finish
                      </d-btn>
                    </div>
                  </div>
                </template>
              </content-module>
            </transition>
          </div>
        </transition>
      </div>
      <profile-card :content="content" class="md:hidden pt-5 pb-10" />
    </div>
  </div>
</template>

<script>
/* eslint-disable no-plusplus */
  import axios from 'axios';
  import {
    ListingItemType,
    ContentListingInteractionType,
  } from '@resources/scripts/constants';
  import { useAuthStore } from '@stores/auth';
  import PaymentModal from '@resources/scripts/components/PaymentModal.vue';
  import QuizModule from './components/QuizModule.vue';
  import ExamModule from './components/ExamModule.vue';
  import ContentModule from './components/ContentModule.vue';
  import ProfileCard from './components/ProfileCard.vue';

  export default {
    parent: true,
    provide() {
      return {
        contentListing: this.content,
      };
    },

    components: {
      QuizModule,
      ExamModule,
      ContentModule,
      ProfileCard,
    },

    props: {
      content: {
        type: Object,
        default: () => ({}),
      },
      hasPurchased: Boolean,
      hasCompleted: Boolean,
    },

    data() {
      return {
        ListingItemType,
        loading: true,
        loadingScreen: true,
        loadingNextStep: false,
        contentItems: [],
        interactions: [],
        totalSteps: 0,
        currentStep: 0,
        earnedCertificate: null,
        showCompletedScreen: false,
        timerCompleted: true,
        startTime: null,
        timeRemaining: 0,
        timerHasHappenedAlready: [],
        auth: null,
      };
    },

    computed: {
      minutes() {
        return Math.floor(this.timeRemaining / 60)?.toFixed(0)?.padStart(2,'0');
      },
        seconds() {
        return (this.timeRemaining % 60)?.toFixed(0)?.padStart(2, '0');
      },
    
      // Use Pinia auth store's profile
      profile() {
        return this.auth ? this.auth.profile || {} : {};
      },

      currentStepContent() {
        return this.contentItems[this.currentStep - 1];
      },

      cost() {
        if (Number.isNaN(this.content.cost) || this.content.cost === 0) {
          return null;
        }
        return this.content.cost.toLocaleString('en-US', {
          style: 'decimal',
          minimumFractionDigits: 2,
        });
      },

      listingItemInteractions() {
        return this.interactions.filter((m) => m.contentListingItemId);
      },
    },

    async mounted() {
      this.auth = useAuthStore();
      if (!this.hasPurchased) return;
      if (this.hasCompleted) {
        await this.getEarnedCertificates();
        this.showCompletedScreen = true;
      }
      await this.getContentItems();
      await this.getInteractions();
      this.loadingScreen = false;
    },

    beforeDestroy() {
      clearInterval(this._interval);
    },

    methods: {
      start() {
        this.currentStep++;
        if (this.currentStepContent.minimumMinutes != null) {
          this.startTimer();
        }
      },
      async getContentItems() {
        const { data } = await axios.get(
          `/api/content-listings/${this.content.id}/items`,
        );
        this.contentItems = data;
        this.totalSteps = data.length;
      },

      async getInteractions() {
        const { data } = await axios.get(
          `/api/content-listings/${this.content.id}/interactions`,
        );
          this.interactions = data;
      },

      async startLessonAgainAfterCompletion() {
          this.showCompletedScreen = false;
          this.interactions = [];
        // await this.deleteQuizResults();
      },

      async deleteQuizResults() {
          await axios.delete(`/api/content-listings/quizzes/${this.content.id}`);
      },

      async saveInteraction() {
        // Skip interaction saving for exams - they have their own progress tracking
        if (this.currentStepContent.type === ListingItemType.EXAM) {
          return;
        }
        
        const interaction = this.interactions.find(
          (m) => m.contentListingItemId === this.currentStepContent.id,
        );
        try {
          if (!interaction) {
            await axios.post(
              `/api/content-listings/${this.content.id}/interactions`,
              {
                startTime: new Date(),
                contentListingId: this.content.id,
                contentListingItemId: this.currentStepContent.id,
                type: ContentListingInteractionType.VIEW,
                stopTime: new Date(),
              },
            );
          } else if (!interaction.stopTime) {
            await axios.patch(
              `/api/content-listings/interactions/${interaction.id}`,
              {
                stopTime: new Date(),
              },
            );
          }
        } catch (error) {
          this.$toast.error(
            'An error occurred attempting to go to the next step. Please refresh your browser and try again.',
          );
        }
      },

      async processLastStep() {
        this.loadingNextStep = true;
        try {
          await this.saveInteraction();
          const canReceiveCertificate = await this.checkCanReceiveCertificate();
          console.log('canReceiveCertificate:', canReceiveCertificate);
          if (canReceiveCertificate) {
            await this.sendCompleteInteraction();
            await this.createEarnedCertificate();
          } else {
            this.$toast.error(
              'You are not able to finish the lesson at this time. Please verify that you have reviewed all of the lesson materials and have passed each quiz available for this lesson.',
              {
                timeout: 7000
              }
            );
          }
        } catch (error) {
          console.error('Error in processLastStep:', error);
          this.$toast.error(
            `An error occurred attempting to finish this lesson: ${error.message || error}. Please refresh your browser and try again.`,
          );
        } finally {
          this.loadingNextStep = false;
        }
      },
      async sendCompleteInteraction() {
        if (
          !this.interactions.find(
            (x) => x.type === ContentListingInteractionType.COMPLETE,
          )
        ) {
          await axios.post(
            `/api/content-listings/${this.content.id}/interactions`,
            {
              startTime: new Date(),
              contentListingId: this.content.id,
              contentListingItemId: null,
              type: ContentListingInteractionType.COMPLETE,
              stopTime: new Date(),
            },
          );
        }
      },

      async checkCanReceiveCertificate() {
        const { data } = await axios.get(
          `/api/content-listings/${this.content.id}/can-receive-certificate`,
        );
        return data;
      },

      async createEarnedCertificate() {
        if (this.content.grantsCertificate && !this.earnedCertificate) {
          const { data } = await axios.post('/api/earned-certificates', {
            contentListingId: this.content.id,
            userId: this.profile.id,
          });
          if (!data.success) {
            this.$toast.error(
              'An error occurred attempting to create the certificate for this lesson. Please refresh the page and try again.',
            );
            return;
          }
          this.earnedCertificate = data.resultObject;
        }
        this.showCompletedScreen = true;
      },

      async getEarnedCertificates() {
        const { data } = await axios.get(
          `/api/earned-certificates/content-listing?contentListingId=${this.content.id}`,
        );
        this.earnedCertificate = data[0];
      },

      async downloadItem() {
        // Submit the interaction for historical tracking
        if (
          this.profile
          && this.currentStepContent.type === ListingItemType.FILE
        ) {
          await axios.post(
            `/api/content-listings/${this.content.id}/interactions`,
            {
              type: ContentListingInteractionType.DOWNLOAD,
              userId: this.profile.id,
              contentListingId: this.content.id,
              contentListingItemId: this.currentStepContent.id,
              startTime: new Date(),
              stopTime: new Date(),
            },
          );
        }

        // Download the file
        const { data } = await axios.get(
          `/api/content-listings/items/interactive/${this.currentStepContent.id}/download-url`,
        );
        const ios = navigator.userAgent.toLowerCase().match(/(iphone|ipod|ipad)/);
        if (ios) {
          window.location.href = data;
        } else {
          const a = document.createElement('a');
          document.body.appendChild(a);
          a.style = 'display: none';
          a.target = '_blank';
          a.href = data;
          a.click();
          document.body.removeChild(a);
        }
      },

      launchBuyNowModal() {
        this.$modal.open({
          parent: this,
          component: PaymentModal,
          width: 550,
          hasModalCard: true,
          props: {
            selectedContent: this.content,
          },
        });
      },

      goBack() {
        this.currentStep--;
      },

      async goNext() {
        this.loadingNextStep = true;
        await this.saveInteraction();
        this.currentStep++;
        this.loadingNextStep = false;

        // see if we need a timer
        if (this.currentStepContent.minimumMinutes != null) {
          this.startTimer();
        }
      },

      startTimer() {
          // don't redo timers if it's already happened
          if (this.timerHasHappenedAlready.includes(this.currentStepContent.id)) {
              this.timerCompleted = true;
              return;
          }

          this.startTime = Date.now();
          this.timerCompleted = false;
          this._interval = setInterval(() => {
              const elapsed = (Date.now() - this.startTime) / 1000;
              this.timeRemaining = Math.max(0, this.currentStepContent.minimumMinutes * 60 - elapsed);
              if (this.timeRemaining <= 0) {
                  this.timerCompleted = true;
                  this.timerHasHappenedAlready.push(this.currentStepContent.id)
                  clearInterval(this._interval);
              }
              this.$forceUpdate();
          }, 1000);
      },

      goToLastSavedStep() {
        for (let i = 0; i < this.contentItems.length; i++) {
          // First, check if the step has an interaction. If not, then it's the next step.
          const stepFound = this.interactions.find(
            (interaction) => this.contentItems[i].id === interaction.contentListingItemId,
          );
          if (!stepFound) {
            this.currentStep = i + 1;
            break;
          }

          // Second, check if step has no stop time. Otherwise, it's unfinished and the next step.
          const step = this.interactions.find(
            (interaction) => this.contentItems[i].id === interaction.contentListingItemId
              && !interaction.stopTime,
          );
          if (step) {
            this.currentStep = i + 1;
            break;
          }
        }

        // All steps appear to be finished, so let's send them to the last step.
        if (this.currentStep === 0) {
          this.currentStep = this.contentItems.length;
        }
      },
    },
  };
</script>
